import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Zap, ArrowLeft, Loader2, Home, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function NewCharging() {
  const nav = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [homeRate, setHomeRate] = useState(null);
  const [publicOps, setPublicOps] = useState([]);
  const [form, setForm] = useState({
    vehicle_id: "",
    session_at: new Date().toISOString().slice(0, 16),
    odometer: "",
    kwh: "",
    cost: "",
    location_type: "home",
    provider: "",
    start_soc: "",
    end_soc: "",
    notes: "",
  });

  useEffect(() => {
    api.get("/vehicles")
      .then(({ data }) => {
        // Item 6: Charging only for EV/PHEV/Hybrid — strict (no fallback to all)
        const list = (data || []).filter((v) => {
          const pt = (v.powertrain || v.fuel_type || "").toLowerCase();
          return pt === "electric" || pt === "phev" || pt === "hybrid" || pt === "ev";
        });
        setVehicles(list);
        if (list.length && !form.vehicle_id) {
          setForm((f) => ({ ...f, vehicle_id: list[0].id }));
        }
      })
      .catch((e) => toast.error(formatApiError(e)));
    // Load city-aware rate hint
    api.get("/rates/ev-home").then(({ data }) => setHomeRate(data)).catch(() => {});
    api.get("/rates/ev-public").then(({ data }) => setPublicOps(data.operators || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cost_per_kwh = useMemo(() => {
    const c = parseFloat(form.cost);
    const k = parseFloat(form.kwh);
    if (!c || !k) return null;
    return (c / k).toFixed(2);
  }, [form.cost, form.kwh]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) return toast.error("Pick a vehicle");
    if (!form.odometer || !form.kwh) return toast.error("Odometer and kWh are required");
    setSaving(true);
    try {
      const payload = {
        vehicle_id: form.vehicle_id,
        session_at: new Date(form.session_at).toISOString(),
        odometer: parseFloat(form.odometer),
        kwh: parseFloat(form.kwh),
        cost: parseFloat(form.cost) || 0,
        location_type: form.location_type,
        provider: form.provider || null,
        start_soc: form.start_soc ? parseFloat(form.start_soc) : null,
        end_soc: form.end_soc ? parseFloat(form.end_soc) : null,
        notes: form.notes || null,
      };
      await api.post("/charging", payload);
      toast.success("Charging session logged");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto space-y-5" data-testid="new-charging-page">
      <div className="flex items-center gap-3">
        <button
          onClick={() => nav(-1)}
          className="w-9 h-9 border border-[color:var(--border-line,#262626)] hover:bg-[color:var(--bg-elevated,#1A1A1A)] flex items-center justify-center"
          data-testid="new-charging-back"
          aria-label="Back"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <div className="ft-overline text-xs">EV / PHEV</div>
          <h1 className="font-display text-3xl font-black tracking-tighter flex items-center gap-2">
            <Zap className="text-[#22D3EE]" size={26} /> Add Charging
          </h1>
        </div>
      </div>

      {vehicles.length === 0 ? (
        <div className="border border-[color:var(--border-line,#262626)] p-6 text-sm text-[color:var(--text-secondary,#A3A3A3)]" data-testid="new-charging-empty">
          No EV / PHEV / Hybrid vehicles in your account. Add one from Garage → Add Vehicle, or use Add Refuel for petrol/diesel/CNG vehicles.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {/* Suggested rate hint (city / operator) */}
          <div className="rounded-lg border border-[var(--border-line)] bg-[var(--bg-elevated)] p-3 text-xs text-[var(--text-muted)]" data-testid="new-charging-rate-hint">
            {form.location_type === "home" && homeRate ? (
              <span>
                Suggested home rate for <span className="text-white">{homeRate.city}</span> (
                <span className="text-white">{homeRate.discom || "DISCOM"}</span>): <span className="text-[#FF5A1F] font-semibold">₹{homeRate.kwh_rate}/kWh</span>
                {" · "}<span className="text-[10px] uppercase">{homeRate.estimated ? "Estimate" : "Source"}: {homeRate.source || "—"}</span>
              </span>
            ) : form.location_type === "public" && publicOps.length ? (
              <span>
                Public rates today: {publicOps.slice(0, 3).map((o) => `${o.name} ~₹${o.dc_kwh}`).join(" · ")} — override with your actual charge slip.
              </span>
            ) : (
              <span>Enter your actual charge total. RayFuel will compute ₹/kWh automatically.</span>
            )}
          </div>

          <div>
            <label className="ft-overline text-[10px] mb-1 block">VEHICLE</label>
            <select
              className="w-full bg-[color:var(--bg-surface,#121212)] border border-[color:var(--border-line,#262626)] px-3 py-2.5 text-sm"
              value={form.vehicle_id}
              onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })}
              data-testid="new-charging-vehicle"
            >
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>{v.number} — {v.name} ({v.powertrain || v.fuel_type})</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="ft-overline text-[10px] mb-1 block">ODOMETER (km)</label>
              <input
                type="number" step="any" required
                className="w-full bg-[color:var(--bg-surface,#121212)] border border-[color:var(--border-line,#262626)] px-3 py-2.5 text-sm"
                value={form.odometer}
                onChange={(e) => setForm({ ...form, odometer: e.target.value })}
                data-testid="new-charging-odometer"
              />
            </div>
            <div>
              <label className="ft-overline text-[10px] mb-1 block">DATE / TIME</label>
              <input
                type="datetime-local" required
                className="w-full bg-[color:var(--bg-surface,#121212)] border border-[color:var(--border-line,#262626)] px-3 py-2.5 text-sm"
                value={form.session_at}
                onChange={(e) => setForm({ ...form, session_at: e.target.value })}
                data-testid="new-charging-datetime"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="ft-overline text-[10px] mb-1 block">ENERGY (kWh)</label>
              <input
                type="number" step="any" required
                className="w-full bg-[color:var(--bg-surface,#121212)] border border-[color:var(--border-line,#262626)] px-3 py-2.5 text-sm"
                value={form.kwh}
                onChange={(e) => setForm({ ...form, kwh: e.target.value })}
                data-testid="new-charging-kwh"
              />
            </div>
            <div>
              <label className="ft-overline text-[10px] mb-1 block">TOTAL COST (₹)</label>
              <input
                type="number" step="any"
                className="w-full bg-[color:var(--bg-surface,#121212)] border border-[color:var(--border-line,#262626)] px-3 py-2.5 text-sm"
                value={form.cost}
                onChange={(e) => setForm({ ...form, cost: e.target.value })}
                data-testid="new-charging-cost"
              />
            </div>
          </div>
          {cost_per_kwh && (
            <div className="text-xs text-[color:var(--text-secondary,#A3A3A3)]">
              ≈ ₹{cost_per_kwh}/kWh
            </div>
          )}

          <div>
            <label className="ft-overline text-[10px] mb-1 block">LOCATION</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, location_type: "home" })}
                className={`flex items-center justify-center gap-2 py-2.5 text-sm border transition-colors ${form.location_type === "home" ? "bg-[color:var(--accent,#FF5A1F)] text-[color:var(--accent-fg,#000)] border-[color:var(--accent,#FF5A1F)]" : "border-[color:var(--border-line,#262626)] text-[color:var(--text-secondary,#A3A3A3)] hover:text-white"}`}
                data-testid="new-charging-loc-home"
              >
                <Home size={16} /> Home
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, location_type: "public" })}
                className={`flex items-center justify-center gap-2 py-2.5 text-sm border transition-colors ${form.location_type === "public" ? "bg-[color:var(--accent,#FF5A1F)] text-[color:var(--accent-fg,#000)] border-[color:var(--accent,#FF5A1F)]" : "border-[color:var(--border-line,#262626)] text-[color:var(--text-secondary,#A3A3A3)] hover:text-white"}`}
                data-testid="new-charging-loc-public"
              >
                <MapPin size={16} /> Public
              </button>
            </div>
          </div>

          {form.location_type === "public" && (
            <div>
              <label className="ft-overline text-[10px] mb-1 block">PROVIDER</label>
              <input
                type="text" placeholder="Tata Power, Ather, ChargeZone…"
                className="w-full bg-[color:var(--bg-surface,#121212)] border border-[color:var(--border-line,#262626)] px-3 py-2.5 text-sm"
                value={form.provider}
                onChange={(e) => setForm({ ...form, provider: e.target.value })}
                data-testid="new-charging-provider"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="ft-overline text-[10px] mb-1 block">START SoC (%)</label>
              <input
                type="number" step="1" min="0" max="100"
                className="w-full bg-[color:var(--bg-surface,#121212)] border border-[color:var(--border-line,#262626)] px-3 py-2.5 text-sm"
                value={form.start_soc}
                onChange={(e) => setForm({ ...form, start_soc: e.target.value })}
                data-testid="new-charging-start-soc"
              />
            </div>
            <div>
              <label className="ft-overline text-[10px] mb-1 block">END SoC (%)</label>
              <input
                type="number" step="1" min="0" max="100"
                className="w-full bg-[color:var(--bg-surface,#121212)] border border-[color:var(--border-line,#262626)] px-3 py-2.5 text-sm"
                value={form.end_soc}
                onChange={(e) => setForm({ ...form, end_soc: e.target.value })}
                data-testid="new-charging-end-soc"
              />
            </div>
          </div>

          <div>
            <label className="ft-overline text-[10px] mb-1 block">NOTES</label>
            <textarea
              rows={2}
              className="w-full bg-[color:var(--bg-surface,#121212)] border border-[color:var(--border-line,#262626)] px-3 py-2.5 text-sm"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="new-charging-notes"
            />
          </div>

          <button
            type="submit" disabled={saving}
            className="w-full bg-[#22D3EE] hover:bg-[#06B6D4] text-black font-bold py-3 flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="new-charging-submit"
          >
            {saving ? <Loader2 className="animate-spin" size={18} /> : <Zap size={18} />}
            {saving ? "Saving…" : "Save Charging Session"}
          </button>
        </form>
      )}
    </div>
  );
}
